import { getEffectiveMapForProduct } from '../../../utils/mapPricing';
import type { CameraWithPricing } from '../../../utils/productDetailHelpers';

/** Epoch ms from a Firestore timestamp, ISO string or Date; null when absent or unparseable. */
export function toMs(value: unknown): number | null {
  if (value == null || value === '') return null;
  const v = value as { toDate?: () => Date; _seconds?: number; seconds?: number };
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  const seconds = v._seconds ?? v.seconds;
  if (typeof seconds === 'number') return seconds * 1000;
  const t = (value instanceof Date ? value : new Date(value as string)).getTime();
  return Number.isFinite(t) ? t : null;
}

export type PricingWindowKind = 'map' | 'rebate' | 'sale';

export interface PricingWindow {
  kind: PricingWindowKind;
  label: string;
  /** Dollars: MAP, rebate amount or sale price. */
  amount: number;
  startMs: number | null;
  /** The last day (or instant) of the window, as stored. */
  endMs: number | null;
  /** First instant after the window (the stored end, read as `getEffectiveMapForProduct` reads it). */
  endExclusiveMs: number | null;
  /** Lowest price a store may advertise while this window is the one running. */
  lowestAd: number | null;
  status: 'live' | 'upcoming' | 'ended';
}

/**
 * First instant after a window. The end is read exactly as stored, the way
 * `getEffectiveMapForProduct` reads it, so the calendar, the MAP cells, the
 * override floor and the advertised price never disagree about whether a
 * rebate is still running.
 */
export const exclusiveEnd = (endMs: number | null): number | null => endMs;

const statusAt = (startMs: number | null, endMs: number | null, nowMs: number): PricingWindow['status'] => {
  const end = exclusiveEnd(endMs);
  if (end != null && end < nowMs) return 'ended';
  if (startMs != null && startMs > nowMs) return 'upcoming';
  return 'live';
};

/**
 * The MAP, instant-rebate and sale windows the catalog carries for one
 * product, in start order. Built only from fields on the product document;
 * nothing is invented, so a product with none returns an empty list.
 */
export function buildPricingWindows(product: CameraWithPricing, nowMs: number = Date.now()): PricingWindow[] {
  const p = product.pricing ?? {};
  if (!product.pricing && !product.pricingData) return [];
  const windows: PricingWindow[] = [];
  // The effective MAP prefers the live pricing sheet over the catalog fields,
  // the same source the price block reads, so the calendar never disagrees with it.
  const em = getEffectiveMapForProduct(product, nowMs);
  const map = em?.raw && em.raw > 0 ? em.raw : Number(p.map ?? 0);
  // When the effective MAP resolves, it alone decides the rebate (a loaded sheet with a zero or
  // cancelled rebate wins over a leftover catalog one). Catalog fields are read only without it.
  const emRebate = !!em && em.instantRebate > 0 && (em.windowStatus === 'active' || em.windowStatus === 'upcoming');
  const rebate = em ? (emRebate ? em.instantRebate : 0) : Number(p.instantRebate ?? 0);
  const rebateStart = em ? toMs(em.windowStart) : toMs(p.rebateStartAt);
  const rebateEnd = em ? toMs(em.windowEnd) : toMs(p.rebateEndAt);

  if (map > 0) {
    const startMs = toMs(p.mapStartAt) ?? toMs(p.manufacturerPriceAsOf);
    // The sheet's own end date is its file validity, not the MAP policy, so only an explicit MAP end counts.
    const endMs = toMs((p as { mapEndAt?: unknown }).mapEndAt);
    windows.push({ kind: 'map', label: 'MAP', amount: map, startMs, endMs, endExclusiveMs: exclusiveEnd(endMs), lowestAd: map, status: statusAt(startMs, endMs, nowMs) });
  }
  if (rebate > 0) {
    windows.push({
      kind: 'rebate',
      label: 'Instant rebate',
      amount: rebate,
      startMs: rebateStart,
      endMs: rebateEnd,
      endExclusiveMs: exclusiveEnd(rebateEnd),
      lowestAd: map > 0 ? Math.max(0, Math.round((map - rebate) * 100) / 100) : null,
      status: statusAt(rebateStart, rebateEnd, nowMs),
    });
  }
  const sale = Number(p.salePrice ?? 0);
  if (sale > 0) {
    const startMs = toMs(p.salePriceStartAt);
    const endMs = toMs(p.salePriceEndAt);
    windows.push({ kind: 'sale', label: 'Sale price', amount: sale, startMs, endMs, endExclusiveMs: exclusiveEnd(endMs), lowestAd: sale, status: statusAt(startMs, endMs, nowMs) });
  }
  // Rebates that already ended (MOT-551): the lookup's `pastRebates`, so the calendar can
  // show when a rebate expired. Always `ended`; a row that is somehow not over yet is the
  // live sheet's business, not this list's. A past window of the SAME amount that overlaps
  // or touches the catalog's own rebate window (an earlier weekly row of a running rebate,
  // or the same window with bare-date vs timed bounds) is that rebate, not an ended one.
  // Every consumer that prices filters `ended` out. Touching is judged on exclusive ends
  // (a bare-date end, stored as UTC midnight, runs through that day; a timed end is itself
  // the last instant) with a twelve-hour allowance, the API's rule: a 12:01 a.m. clock or a
  // bare-date week (read here as UTC midnight) beside a Pacific-clock week still reads as one
  // rebate, while a real day at MAP between two timed rows keeps them apart. A window with no start is read from its
  // end, as the API does, and a window with no dates at all touches nothing, since the API
  // never merges an undated row.
  const DAY_MS = 86_400_000;
  const isBareDate = (ms: number) => ms % DAY_MS === 0;
  const exclusiveEndOf = (endMs: number | null) => (endMs == null ? Infinity : isBareDate(endMs) ? endMs + DAY_MS : endMs + 1);
  const GAP = 12 * 60 * 60 * 1000;
  const touchesExisting = (amount: number, startMs: number | null, endMs: number) =>
    windows.some((w) => {
      if (w.kind !== 'rebate' || w.amount !== amount) return false;
      if (w.startMs == null && w.endMs == null) return false;
      const wStart = w.startMs ?? w.endMs ?? -Infinity;
      const pastStart = startMs ?? endMs;
      return wStart <= exclusiveEndOf(endMs) + GAP && pastStart <= exclusiveEndOf(w.endMs) + GAP;
    });
  for (const past of product.pricingData?.pastRebates ?? []) {
    const amount = Number(past.instantRebate ?? 0);
    const endMs = toMs(past.endDate);
    if (!(amount > 0) || endMs == null || exclusiveEnd(endMs)! >= nowMs) continue;
    const startMs = toMs(past.effectiveDate);
    if (touchesExisting(amount, startMs, endMs)) continue;
    const pastMap = Number(past.mapPrice ?? 0) > 0 ? Number(past.mapPrice) : map;
    // The price it allowed: the API's own after-IR (a sheet's stated after-IR when it had one), else MAP − IR.
    const afterIR = Number(past.sellingPriceAfterIR ?? 0);
    windows.push({
      kind: 'rebate',
      label: 'Instant rebate',
      amount,
      startMs,
      endMs,
      endExclusiveMs: exclusiveEnd(endMs),
      lowestAd: afterIR > 0 ? afterIR : pastMap > 0 ? Math.max(0, Math.round((pastMap - amount) * 100) / 100) : null,
      status: 'ended',
    });
  }
  return windows.sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0));
}

/** MAP minus any live instant rebate: the floor an override is judged against. */
export function mapFloorFor(product: CameraWithPricing, nowMs: number = Date.now()): number | null {
  const em = getEffectiveMapForProduct(product, nowMs);
  return em && em.floor > 0 ? em.floor : null;
}
